import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Download, FileText, RefreshCw, ShieldCheck, Wrench } from "lucide-react";
import { format, startOfDay, subDays } from "date-fns";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/audit")({
  head: () => ({ meta: [{ title: "Audit / Ledger Repair — Admin" }] }),
  component: AuditPage,
});

// ---------- helpers ----------
function fmtINR(n: number | string | null | undefined) {
  const v = Number(n ?? 0);
  const sign = v < 0 ? "−" : "";
  return `${sign}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
}
function fmtDT(iso?: string | null) {
  if (!iso) return "—";
  try { return format(new Date(iso), "dd MMM yyyy HH:mm"); } catch { return iso; }
}
function sevTone(s: string) {
  switch (s) {
    case "critical": return "bg-red-100 text-red-800 border-red-300";
    case "high": return "bg-orange-100 text-orange-800 border-orange-300";
    case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-300";
    default: return "bg-slate-100 text-slate-700 border-slate-300";
  }
}
function statusTone(s: string) {
  switch (s) {
    case "resolved": return "bg-emerald-100 text-emerald-800 border-emerald-300";
    case "investigating": return "bg-blue-100 text-blue-800 border-blue-300";
    case "ignored": return "bg-slate-100 text-slate-600 border-slate-300";
    default: return "bg-red-50 text-red-700 border-red-200";
  }
}
function issueTypeLabel(t: string) {
  return t.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
function rootCauseLabel(c: string) { return c.replaceAll("_", " "); }

type Issue = {
  id: string;
  issue_type: string;
  severity: string;
  status: string;
  user_id: string;
  mobile: string | null;
  game_id: string | null;
  game_name: string | null;
  issue_date: string | null;
  expected_amount: number;
  actual_amount: number;
  difference_amount: number;
  root_cause_code: string;
  root_cause_message: string | null;
  confidence: string;
  source_route: string | null;
  source_function: string | null;
  source_table: string | null;
  app_version: string | null;
  device_info: any;
  affected_ids: any;
  evidence: any;
  detected_at: string;
  first_divergence_at: string | null;
  last_seen_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
};

const REPAIR_OPTIONS: { value: string; label: string; hint: string; requiresIssueType?: string[] }[] = [
  { value: "RECALCULATE_WALLET_FROM_LEDGER", label: "Recalculate wallet from ledger", hint: "Overwrites wallet_balance with the sum of verified ledger entries. Use only when the ledger is complete." },
  { value: "REVERSE_DUPLICATE_DEBIT", label: "Reverse duplicate debit", hint: "Inserts a refund equal to the extra debit. Use only when duplicate wallet debit is proven.", requiresIssueType: ["BET_DEBIT_MISMATCH"] },
  { value: "RECREATE_MISSING_LEDGER", label: "Recreate missing ledger entry", hint: "Debits wallet and inserts the missing 'bet' ledger row. Requires sufficient wallet balance.", requiresIssueType: ["BET_DEBIT_MISMATCH"] },
  { value: "CREDIT_MISSING_WINNING", label: "Credit missing winning", hint: "Credits the payout for a won bet whose win transaction is missing.", requiresIssueType: ["WIN_CREDIT_MISSING"] },
  { value: "REVERSE_DUPLICATE_WINNING", label: "Reverse duplicate winning", hint: "Reverses excess winning credit when a payout was credited twice.", requiresIssueType: ["WIN_CREDIT_MISSING", "BALANCE_LEDGER_MISMATCH"] },
  { value: "MARK_RESOLVED", label: "Mark resolved without financial change", hint: "Closes this issue with a note. No wallet/ledger change.", },
];

function AuditPage() {
  const qc = useQueryClient();
  const today = new Date();
  const [from, setFrom] = useState<string>(format(subDays(startOfDay(today), 30), "yyyy-MM-dd"));
  const [to, setTo] = useState<string>(format(today, "yyyy-MM-dd"));
  const [mobile, setMobile] = useState("");
  const [status, setStatus] = useState<string>("open");
  const [severity, setSeverity] = useState<string>("all");
  const [type, setType] = useState<string>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [drawerIssue, setDrawerIssue] = useState<Issue | null>(null);
  const [repairIssue, setRepairIssue] = useState<Issue | null>(null);

  // Load issues
  const { data: issues = [], isFetching, refetch } = useQuery<Issue[]>({
    queryKey: ["audit_issues", { status, severity, type, mobile }],
    queryFn: async () => {
      let q: any = (supabase as any).from("audit_issues").select("*").order("detected_at", { ascending: false }).limit(300);
      if (status !== "all") q = q.eq("status", status);
      if (severity !== "all") q = q.eq("severity", severity);
      if (type !== "all") q = q.eq("issue_type", type);
      if (mobile.trim()) q = q.ilike("mobile", `%${mobile.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Issue[];
    },
  });

  const { data: recentLogs = [] } = useQuery({
    queryKey: ["audit_recent_logs"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("wallet_repair_logs")
        .select("id, repair_type, admin_phone, reason, before_state, after_state, created_at")
        .order("created_at", { ascending: false }).limit(15);
      if (error) throw error;
      return data ?? [];
    },
  });

  const recheck = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("detect_audit_issues" as any, { _from: from, _to: to } as any);
      if (error) throw error;
      return data as any;
    },
    onSuccess: (r: any) => {
      toast.success(`Recheck done — created ${r.created}, updated ${r.updated}, resolved ${r.resolved} (${r.duration_ms}ms)`);
      qc.invalidateQueries({ queryKey: ["audit_issues"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Recheck failed"),
  });

  const counts = useMemo(() => {
    const c = { total: issues.length, critical: 0, open: 0, resolved: 0, users: new Set<string>(), diff: 0 };
    for (const i of issues) {
      if (i.severity === "critical") c.critical++;
      if (i.status === "open" || i.status === "investigating") c.open++;
      if (i.status === "resolved") c.resolved++;
      c.users.add(i.user_id);
      c.diff += Math.abs(Number(i.difference_amount || 0));
    }
    return c;
  }, [issues]);

  function exportCsv() {
    const header = ["id","type","severity","status","mobile","user_id","date","expected","actual","difference","root_cause","confidence","detected_at","resolved_at","source_route","source_function"];
    const rows = issues.map(i => [i.id, i.issue_type, i.severity, i.status, i.mobile ?? "", i.user_id, i.issue_date ?? "", i.expected_amount, i.actual_amount, i.difference_amount, i.root_cause_code, i.confidence, i.detected_at, i.resolved_at ?? "", i.source_route ?? "", i.source_function ?? ""]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replaceAll('"','""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-issues-${format(new Date(),"yyyyMMdd-HHmm")}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-lg font-bold">Audit / Ledger Repair</h1>
            <p className="text-xs text-muted-foreground">Investigation, root-cause and safe repair</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!issues.length}>
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
          <Button size="sm" onClick={() => recheck.mutate()} disabled={recheck.isPending}>
            <RefreshCw className={`h-4 w-4 mr-1 ${recheck.isPending ? "animate-spin" : ""}`} />
            Recheck range
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Stat label="Total" value={String(counts.total)} />
        <Stat label="Critical" value={String(counts.critical)} tone="bad" />
        <Stat label="Open" value={String(counts.open)} tone="warn" />
        <Stat label="Resolved" value={String(counts.resolved)} tone="good" />
        <Stat label="Users affected" value={String(counts.users.size)} />
        <Stat label="Total |Δ|" value={fmtINR(counts.diff)} />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-card p-3 shadow-card sm:grid-cols-4 lg:grid-cols-7">
        <div><Label className="text-[11px]">From</Label><Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} /></div>
        <div><Label className="text-[11px]">To</Label><Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} /></div>
        <div><Label className="text-[11px]">Mobile</Label><Input value={mobile} onChange={(e)=>setMobile(e.target.value)} placeholder="+91…" /></div>
        <div>
          <Label className="text-[11px]">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Severity</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="BET_DEBIT_MISMATCH">Bet ↔ Debit mismatch</SelectItem>
              <SelectItem value="BALANCE_LEDGER_MISMATCH">Wallet ↔ Ledger mismatch</SelectItem>
              <SelectItem value="WIN_CREDIT_MISSING">Winning credit missing</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Issue list */}
      <div className="space-y-2">
        {isFetching && !issues.length && <p className="text-sm text-muted-foreground">Loading issues…</p>}
        {!isFetching && !issues.length && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              <p className="text-sm font-semibold text-emerald-800">No issues match the current filters.</p>
            </div>
            <p className="mt-1 text-xs text-emerald-700">Try “Recheck range” to scan a new date range.</p>
          </div>
        )}
        {issues.map((i) => (
          <IssueCard
            key={i.id} issue={i}
            expanded={!!expanded[i.id]}
            onToggle={() => setExpanded(s => ({ ...s, [i.id]: !s[i.id] }))}
            onDetails={() => setDrawerIssue(i)}
            onRepair={() => setRepairIssue(i)}
          />
        ))}
      </div>

      {/* Repair logs */}
      <div className="rounded-xl bg-card p-3 shadow-card">
        <div className="mb-2 flex items-center gap-2">
          <Wrench className="h-4 w-4 text-primary" />
          <p className="text-sm font-bold">Recent repair actions</p>
        </div>
        <ul className="divide-y text-xs">
          {recentLogs.map((l: any) => (
            <li key={l.id} className="grid grid-cols-[1fr_auto] gap-2 py-2">
              <div className="min-w-0">
                <p className="font-semibold">{l.repair_type} · {l.admin_phone ?? "admin"}</p>
                <p className="text-muted-foreground truncate">{l.reason}</p>
              </div>
              <p className="text-right text-muted-foreground">{fmtDT(l.created_at)}</p>
            </li>
          ))}
          {!recentLogs.length && <li className="py-3 text-center text-muted-foreground">No repairs executed yet.</li>}
        </ul>
      </div>

      {/* Validation details (per-transaction PASS/FAIL) */}
      <ValidationDetailsPanel from={from} to={to} />

      {/* Details drawer */}
      <IssueDrawer issue={drawerIssue} onClose={() => setDrawerIssue(null)} />

      {/* Repair dialog */}
      <RepairDialog issue={repairIssue} onClose={() => setRepairIssue(null)} onDone={() => { qc.invalidateQueries({ queryKey: ["audit_issues"] }); qc.invalidateQueries({ queryKey: ["audit_recent_logs"] }); }} />
    </div>
  );
}

// ------------ Validation details ------------
type VRow = {
  kind: "BET"|"WIN"|"DEPOSIT"|"WITHDRAW"|"REFUND";
  ref_id: string; user_id: string; mobile: string | null;
  amount: number; created_at: string;
  bet_id: string | null; wallet_tx_id: string | null; winning_tx_id: string | null;
  status: "PASS"|"FAIL"|"LEGACY"; reason: string; suggested_repair: string;
};

function ValidationDetailsPanel({ from, to }: { from: string; to: string }) {
  const [onlyFailed, setOnlyFailed] = useState(true);
  const [kind, setKind] = useState<string>("all");
  const q = useQuery({
    queryKey: ["validation_details", from, to, onlyFailed],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("validate_transactions_report" as any, {
        _from: from, _to: to, _limit: 1000, _only_failed: onlyFailed,
      });
      if (error) throw error;
      return ((data as any)?.rows ?? []) as VRow[];
    },
    staleTime: 30_000,
  });
  const rows = useMemo(() => {
    const src = q.data ?? [];
    return kind === "all" ? src : src.filter((r) => r.kind === kind);
  }, [q.data, kind]);

  const passCount = (q.data ?? []).filter(r => r.status === "PASS").length;
  const failCount = (q.data ?? []).filter(r => r.status === "FAIL").length;
  const legacyCount = (q.data ?? []).filter(r => r.status === "LEGACY").length;

  return (
    <div className="rounded-xl bg-card p-3 shadow-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <p className="text-sm font-bold">Validation details (ID-based)</p>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">PASS {passCount}</Badge>
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">FAIL {failCount}</Badge>
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">LEGACY {legacyCount}</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={onlyFailed} onChange={(e)=>setOnlyFailed(e.target.checked)} />
            Only failed
          </label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-7 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="BET">Bet</SelectItem>
              <SelectItem value="WIN">Win</SelectItem>
              <SelectItem value="DEPOSIT">Deposit</SelectItem>
              <SelectItem value="WITHDRAW">Withdraw</SelectItem>
              <SelectItem value="REFUND">Refund</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/70 text-left">
            <tr>
              <th className="px-2 py-1.5">Kind</th>
              <th className="px-2 py-1.5">User</th>
              <th className="px-2 py-1.5">Bet ID</th>
              <th className="px-2 py-1.5">Wallet Tx</th>
              <th className="px-2 py-1.5">Win Tx</th>
              <th className="px-2 py-1.5">Amount</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">Reason / Suggested repair</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Loading…</td></tr>}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">No transactions match the filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.ref_id}`} className="border-t">
                <td className="px-2 py-1.5 font-semibold">{r.kind}</td>
                <td className="px-2 py-1.5">{r.mobile ?? r.user_id.slice(0,8)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.bet_id?.slice(0,8) ?? "—"}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.wallet_tx_id?.slice(0,8) ?? "—"}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.winning_tx_id?.slice(0,8) ?? "—"}</td>
                <td className="px-2 py-1.5">{fmtINR(r.amount)}</td>
                <td className="px-2 py-1.5">
                  <Badge variant="outline" className={
                    r.status === "PASS" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : r.status === "FAIL" ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                  }>{r.status}</Badge>
                </td>
                <td className="px-2 py-1.5">
                  <p>{r.reason}</p>
                  {r.suggested_repair && r.status === "FAIL" && (
                    <p className="text-[10px] text-muted-foreground">→ {r.suggested_repair}</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------ Issue card ------------
function IssueCard({ issue, expanded, onToggle, onDetails, onRepair }: { issue: Issue; expanded: boolean; onToggle: () => void; onDetails: () => void; onRepair: () => void; }) {
  return (
    <div className="rounded-xl border bg-card p-3 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`border ${sevTone(issue.severity)}`} variant="outline">{issue.severity.toUpperCase()}</Badge>
            <Badge className={`border ${statusTone(issue.status)}`} variant="outline">{issue.status}</Badge>
            <span className="text-xs font-semibold">{issueTypeLabel(issue.issue_type)}</span>
            <span className="text-[11px] text-muted-foreground">#{issue.id.slice(0, 8)}</span>
          </div>
          <p className="mt-1 text-sm font-semibold">
            {issue.mobile ?? "—"}{issue.issue_date ? ` · ${issue.issue_date}` : ""}{issue.game_name ? ` · ${issue.game_name}` : ""}
          </p>
          <p className="text-xs text-muted-foreground">{issue.root_cause_message}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Difference</p>
          <p className={`text-base font-bold ${Number(issue.difference_amount) < 0 ? "text-red-700" : "text-orange-700"}`}>{fmtINR(issue.difference_amount)}</p>
          <p className="text-[11px] text-muted-foreground">Expected {fmtINR(issue.expected_amount)} · Actual {fmtINR(issue.actual_amount)}</p>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-2 text-[11px] sm:grid-cols-3">
          <KV k="Root cause" v={rootCauseLabel(issue.root_cause_code)} />
          <KV k="Confidence" v={issue.confidence} />
          <KV k="Detected" v={fmtDT(issue.detected_at)} />
          <KV k="Last seen" v={fmtDT(issue.last_seen_at)} />
          <KV k="Source route" v={issue.source_route ?? "—"} />
          <KV k="Source function" v={issue.source_function ?? "—"} />
          <KV k="Source table" v={issue.source_table ?? "—"} />
          <KV k="User id" v={issue.user_id.slice(0, 8) + "…"} />
          <KV k="Resolved by" v={issue.resolved_by ? issue.resolved_by.slice(0, 8) + "…" : "—"} />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onToggle}>
          {expanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
          {expanded ? "Hide" : "Details"}
        </Button>
        <Button variant="outline" size="sm" onClick={onDetails}>
          <FileText className="h-4 w-4 mr-1" /> Timeline
        </Button>
        {issue.status !== "resolved" && (
          <Button size="sm" onClick={onRepair}>
            <Wrench className="h-4 w-4 mr-1" /> Investigate / Repair
          </Button>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground">{k}</p>
      <p className="truncate font-medium">{v}</p>
    </div>
  );
}
function Stat({ label, value, tone }: { label: string; value: string; tone?: "good"|"bad"|"warn" }) {
  const cls = tone === "bad" ? "text-red-700" : tone === "warn" ? "text-orange-700" : tone === "good" ? "text-emerald-700" : "text-foreground";
  return (
    <div className="rounded-lg border bg-card p-2">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm font-bold ${cls}`}>{value}</p>
    </div>
  );
}

// ------------ Details drawer with timeline ------------
function IssueDrawer({ issue, onClose }: { issue: Issue | null; onClose: () => void }) {
  const open = !!issue;
  const { data, isFetching } = useQuery({
    queryKey: ["audit_timeline", issue?.user_id, issue?.issue_date],
    enabled: open,
    queryFn: async () => {
      const from = issue?.issue_date
        ? new Date(new Date(issue.issue_date).getTime() - 2 * 86400_000).toISOString()
        : new Date(Date.now() - 30 * 86400_000).toISOString();
      const to = issue?.issue_date
        ? new Date(new Date(issue.issue_date).getTime() + 3 * 86400_000).toISOString()
        : new Date().toISOString();
      const { data, error } = await supabase.rpc("get_user_transaction_timeline" as any, { _user_id: issue!.user_id, _from: from, _to: to } as any);
      if (error) throw error;
      return data as any;
    },
  });

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle>Issue #{issue?.id.slice(0, 8)}</SheetTitle></SheetHeader>
        {issue && (
          <div className="mt-4 space-y-4 text-sm">
            <div className="rounded-lg bg-muted p-3">
              <p className="font-semibold">{issue.mobile ?? "—"} · {issueTypeLabel(issue.issue_type)}</p>
              <p className="text-xs text-muted-foreground">{issue.root_cause_message}</p>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">Expected</p><p className="font-semibold">{fmtINR(issue.expected_amount)}</p></div>
                <div><p className="text-muted-foreground">Actual</p><p className="font-semibold">{fmtINR(issue.actual_amount)}</p></div>
                <div><p className="text-muted-foreground">Difference</p><p className="font-bold text-red-700">{fmtINR(issue.difference_amount)}</p></div>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Affected IDs</p>
              <pre className="rounded bg-muted p-2 text-[11px] overflow-x-auto">{JSON.stringify(issue.affected_ids, null, 2)}</pre>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Timeline</p>
              {isFetching && <p className="text-xs text-muted-foreground">Loading…</p>}
              <ul className="divide-y">
                {(data?.events ?? []).map((e: any) => (
                  <li key={e.id} className="py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold uppercase">{e.event_type}</span>
                      <span className="text-muted-foreground">{fmtDT(e.created_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between">
                      <span className="text-muted-foreground">{e.source_function ?? "—"}</span>
                      <span className="font-mono">{fmtINR(e.amount)}</span>
                    </div>
                    {e.metadata?.status && <p className="text-[10px] text-muted-foreground">status: {e.metadata.status}{e.metadata?.note ? ` · ${e.metadata.note}` : ""}</p>}
                  </li>
                ))}
                {!isFetching && !(data?.events?.length) && <li className="py-3 text-center text-xs text-muted-foreground">No timeline events.</li>}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ------------ Repair dialog: preview + confirm ------------
function RepairDialog({ issue, onClose, onDone }: { issue: Issue | null; onClose: () => void; onDone: () => void }) {
  const open = !!issue;
  const suggested = useMemo(() => {
    if (!issue) return "MARK_RESOLVED";
    switch (issue.root_cause_code) {
      case "DUPLICATE_WALLET_DEBIT": return "REVERSE_DUPLICATE_DEBIT";
      case "BET_SAVED_DEBIT_MISSING":
      case "MISSING_LEDGER_ENTRY": return "RECREATE_MISSING_LEDGER";
      case "RESULT_CREDIT_MISSING": return "CREDIT_MISSING_WINNING";
      case "DIRECT_WALLET_UPDATE": return "RECALCULATE_WALLET_FROM_LEDGER";
      case "WRONG_DEBIT_AMOUNT": return "REVERSE_DUPLICATE_DEBIT";
      default: return "MARK_RESOLVED";
    }
  }, [issue]);
  const [repairType, setRepairType] = useState<string>(suggested);
  const [reason, setReason] = useState<string>("");
  const [preview, setPreview] = useState<any | null>(null);

  // Reset when a new issue is opened
  useMemo(() => { setRepairType(suggested); setPreview(null); setReason(""); }, [issue?.id]); // eslint-disable-line

  const availableOptions = useMemo(() => {
    if (!issue) return REPAIR_OPTIONS;
    return REPAIR_OPTIONS.filter(o => !o.requiresIssueType || o.requiresIssueType.includes(issue.issue_type));
  }, [issue]);


  const previewMut = useMutation({
    mutationFn: async () => {
      if (!issue) throw new Error("No issue");
      if (reason.trim().length < 5) throw new Error("Reason must be at least 5 characters");
      const { data, error } = await supabase.rpc("preview_wallet_repair" as any, { _issue_id: issue.id, _repair_type: repairType, _reason: reason } as any);
      if (error) throw error;
      return data as any;
    },
    onSuccess: (d) => setPreview(d),
    onError: (e: any) => toast.error(e?.message ?? "Preview failed"),
  });

  const executeMut = useMutation({
    mutationFn: async () => {
      if (!preview?.preview_id) throw new Error("Generate preview first");
      const idempotency_key = `repair:${preview.preview_id}:${Date.now()}`;
      const { data, error } = await supabase.rpc("execute_wallet_repair" as any, { _preview_id: preview.preview_id, _idempotency_key: idempotency_key, _reason: reason } as any);
      if (error) throw error;
      return data as any;
    },
    onSuccess: (d: any) => {
      if (d?.ok === false) { toast.error(d.error ?? "Repair rejected"); return; }
      toast.success("Repair executed successfully");
      setPreview(null); setReason(""); onDone(); onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? "Repair failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPreview(null); setReason(""); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Investigate & Repair</DialogTitle>
          <DialogDescription>All financial repairs are atomic and logged. Only main admins can execute money changes.</DialogDescription>
        </DialogHeader>

        {issue && (
          <div className="space-y-3 text-sm">
            <div className="rounded bg-muted p-2 text-xs">
              <p className="font-semibold">{issue.mobile ?? "—"} · {issueTypeLabel(issue.issue_type)}</p>
              <p className="text-muted-foreground">{issue.root_cause_message}</p>
            </div>

            <div>
              <Label className="text-xs">Repair type</Label>
              <Select value={repairType} onValueChange={(v) => { setRepairType(v); setPreview(null); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">{availableOptions.find(o=>o.value===repairType)?.hint}</p>
            </div>

            <div>
              <Label className="text-xs">Admin reason (required, min 5 chars)</Label>
              <Textarea value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="e.g. Verified duplicate debit on 2026-07-13; refunding ₹950 per ledger evidence." />
            </div>

            {!preview && (
              <Button className="w-full" onClick={()=>previewMut.mutate()} disabled={previewMut.isPending || reason.trim().length < 5}>
                {previewMut.isPending ? "Generating preview…" : "Preview repair"}
              </Button>
            )}

            {preview && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs">
                <p className="mb-1 flex items-center gap-1 font-bold text-amber-900"><AlertTriangle className="h-4 w-4" /> Confirm repair</p>
                <div className="grid grid-cols-2 gap-2">
                  <KV k="Current wallet" v={fmtINR(preview.current_state?.wallet_balance)} />
                  <KV k="Current ledger" v={fmtINR(preview.current_state?.ledger_balance)} />
                  <KV k="Difference now" v={fmtINR(preview.current_state?.difference)} />
                  <KV k="Diff after repair" v={fmtINR(preview.expected_difference_after)} />
                </div>
                <p className="mt-2 font-semibold">Proposed action</p>
                <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-[10px]">{JSON.stringify(preview.proposed_state, null, 2)}</pre>
                <p className="mt-2 text-amber-800">This will run as an atomic database transaction with idempotency key. Historical repair log will be written.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { setPreview(null); setReason(""); onClose(); }}>Cancel</Button>
          {preview && (
            <Button onClick={() => executeMut.mutate()} disabled={executeMut.isPending}>
              {executeMut.isPending ? "Executing…" : "Confirm & execute repair"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
